import { Role } from 'auth';
import classNames from 'classnames';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { transactionsFilters } from '@/modules/issuer-dashboard/components/holder-tabs/TransactionsTab.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import {
  FiltersPanel,
  TransactionsHeaderAction,
} from '@/modules/issuer-dashboard/pages/IssuerTransactions.tsx';
import { useIssuerActivitiesListQuery } from '@/modules/issuer-dashboard/queries/activities.tsx';
import { usePaymentArchiveByIdMutation } from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  BodyCell,
  ConfirmModal,
  ModalProviderContext,
  PageLayout,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
} from 'ui';
import { useSaveState } from 'ui/hooks';
import { TrashGrayIcon } from 'ui/icons';
import { goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import {
  ActivitiesPaginateResponse,
  ActivitiesType,
  IssuerActivitiesListV1SrActivitiesIssuerGetParams,
  TransactionStatus,
} from 'api';

const IssuerDistributionsPayments = () => {
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();
  return (
    <PageLayout
      title="Payments"
      headerAction={
        <TransactionsHeaderAction
          buttonName="Add new payment"
          openTypeTransaction="Payment"
          className="w-[170px]"
          isDeleted={isDeleted}
          setIsDeleted={setIsDeleted}
        />
      }
      contentClassName="px-0 py-2"
      classNameTitle="flex-row justify-between items-center"
    >
      <PaymentsBody isDeleted={isDeleted} />
    </PageLayout>
  );
};

type TypeHead =
  | 'ID'
  | 'Status'
  | 'Distribution event ID'
  | 'Class'
  | 'Holder'
  | 'Date'
  | 'Amount'
  | '';

interface PaymentsBodyProps {
  rightBlock?: ReactNode;
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
  isDeleted?: boolean;
}

export const PaymentsBody = ({
  rightBlock,
  hideChooseRegisterClass = false,
  register_class_id,
  isDeleted,
}: PaymentsBodyProps) => {
  const [openFull, setOpenFull] = useState(false);
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [filters, setFilters] = useSaveState<transactionsFilters>(
    {
      register_class_id,
      status: TransactionStatus.Completed,
    },
    `filters-payments-${registerId}`,
  );

  const [activity, setActivity] = useState<ActivitiesPaginateResponse>();
  const [archivingId, setArchivingId] = useState<number | null>(null);

  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingActivitiesList,
    isFetching,
    isError,
  } = useIssuerActivitiesListQuery(
    {
      page: 1,
      page_size: 30,
      ...filters,
      register_class_id: register_class_id ?? filters.register_class_id,
      is_deleted: isDeleted,
      is_dividend_activities: true,
      type: ActivitiesType.FundToInvestor,
    } as IssuerActivitiesListV1SrActivitiesIssuerGetParams,
    false,
    Number(registerId),
  );

  const { mutateAsync: mutateAsyncPaymentArchive, isLoading: isLoadingPaymentArchive } =
    usePaymentArchiveByIdMutation();

  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    let items: TableHeadColumn<TypeHead>[] = [
      { title: 'ID', className: '' },
      { title: 'Status', className: '' },
      { title: 'Distribution event ID', className: '' },
      {
        title: 'Class',
        className: '',
      },
      {
        title: 'Holder',
        className: '',
      },
      {
        title: 'Date',
        className: 'min-w-[140px]',
      },
      {
        title: 'Amount',
        className: '',
      },
      {
        title: '',
      },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    if (isDeleted) {
      items = items.slice(0, -1);
    }

    return items;
  }, [hideChooseRegisterClass, isDeleted]);

  const handleOpenFullDividend = useCallback(
    (id: number) => () => {
      openModal(DividendOperationInfoModal, {
        id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const handleArchive = useCallback(
    (id: number) => {
      openModal(ConfirmModal, {
        title: 'Archive payment?',
        onConfirm: async () => {
          try {
            setArchivingId(id);
            await mutateAsyncPaymentArchive(id);
          } finally {
            setArchivingId(null);
          }
        },
      });
    },
    [mutateAsyncPaymentArchive, openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerActivitiesList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerActivitiesList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          let body: BodyCell[] = [
            {
              cell: (
                <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">#{data.id}</span>
              ),
            },
            {
              cell: (
                <span>
                  <ActivityStatusBadge status={data.status || ''} />
                </span>
              ),
            },
            {
              cell: (
                <span
                  className={classNames('text-sm leading-[22px] text-[#0369A1] font-[500]', {
                    'cursor-pointer hover:underline': !!data.payment_dividend_event_id,
                  })}
                  onClick={
                    data.payment_dividend_event_id
                      ? handleOpenFullDividend(data.payment_dividend_event_id)
                      : undefined
                  }
                >
                  #{data.payment_dividend_event_id}
                </span>
              ),
            },
            {
              cell: <span>{data.register_class_name ?? '-'}</span>,
            },
            {
              cell: <div className="">{data.from_entity_name}</div>,
            },
            {
              cell: (
                <div className="">
                  {dateToPrint(data.completion_date || data.requested_at_date, DateVariant.Table) ||
                    '-'}
                </div>
              ),
            },
            {
              cell: <span>{goodFormatUSDCurrencyLowPrecision(data.amount)}</span>,
            },
            {
              cell: (
                <span>
                  {isLoadingPaymentArchive && archivingId === data.id ? (
                    <Spinner size="sm" className="m-0" />
                  ) : (
                    <TooltipNew text="Archive" className="w-5 h-5 min-w-5 min-h-5">
                      <img
                        src={TrashGrayIcon}
                        alt="Archive"
                        className="w-5 h-5 min-w-5 min-h-5 cursor-pointer hover:opacity-75"
                        onClick={e => {
                          e.stopPropagation();
                          handleArchive(data.id);
                        }}
                      />
                    </TooltipNew>
                  )}
                </span>
              ),
            },
          ];

          if (hideChooseRegisterClass) {
            body = body.filter((_, i) => i !== 3); //remove class
          }

          if (isDeleted) {
            body = body.slice(0, -1);
          }

          return {
            cells: body,
            onClick: () => {
              setActivity(data);
              setOpenFull(true);
            },
          };
        }),
      );
    });

    return result;
  }, [
    archivingId,
    handleArchive,
    handleOpenFullDividend,
    hideChooseRegisterClass,
    isDeleted,
    isLoadingPaymentArchive,
    issuerActivitiesList,
  ]);

  return (
    <>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
          <FiltersPanel
            setFilters={setFilters}
            filters={filters}
            registerId={registerId}
            isFetching={isFetching}
            hideChooseRegisterClass={hideChooseRegisterClass}
            forTransfers={true}
          />
          {rightBlock}
        </div>
        <Table
          className="my-0"
          classNameHead="border-t-0"
          withRounded={false}
          withBorderX={false}
          headItems={headItems}
          items={bodyItems}
          isLoading={isLoadingActivitiesList}
          isError={isError}
          hasNextPageInfinite={hasNextPage}
          fetchNextPageInfinite={fetchNextPage}
          blockIdInfinite="scrollable"
        />
      </div>
      {!!activity?.payment_id && (
        <IssuerActivitiesOperationInfoModal
          paymentId={activity.payment_id}
          registerId={registerId}
          registerCountry={registerCountry}
          open={openFull}
          onClose={() => {
            setOpenFull(false);
            setActivity(undefined);
          }}
          isDeleted={isDeleted}
        />
      )}
    </>
  );
};

export default withPrivateAccess(IssuerDistributionsPayments, [
  Role.SR_ISSUER,
  Role.SR_ISSUER_ADMIN,
]);
